import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CONTAINER_DISPATCH_DATE_SAVED_EVENT } from '../utils/containerDispatchEvents';
import { socket } from '../socket';
import { auth } from '../firebaseConfig';

interface ContainerDispatchSidebarProps {
  open: boolean;
  collapsed?: boolean;
  selectedDate?: string;
  onClose: () => void;
  onToggleCollapse?: () => void;
  onSelectDate: (date: string | undefined) => void;
}

function getKoreanDayShort(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ko-KR', { weekday: 'short' });
}

function getTodayYmdSeoul(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
}

function normalizeDateString(raw: unknown): string {
  if (raw == null) return '';
  const s = String(raw).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

const ContainerDispatchSidebar: React.FC<ContainerDispatchSidebarProps> = ({
  open,
  collapsed = false,
  selectedDate,
  onClose,
  onToggleCollapse,
  onSelectDate,
}) => {
  const [dates, setDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    const weekAhead = new Date();
    weekAhead.setDate(weekAhead.getDate() + 7);
    return {
      start: threeDaysAgo.toISOString().split('T')[0],
      end: weekAhead.toISOString().split('T')[0],
    };
  });
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleteStep, setDeleteStep] = useState<1 | 2 | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [exportedDates, setExportedDates] = useState<string[]>([]);
  const [exportCountByDate, setExportCountByDate] = useState<Record<string, number>>({});
  const [newReportDate, setNewReportDate] = useState(() => getTodayYmdSeoul());
  const dateRangeRef = useRef(dateRange);
  const fetchDatesRef = useRef<() => Promise<void>>(async () => {});
  dateRangeRef.current = dateRange;

  const fetchDates = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/container-dispatch/dates?start=${dateRange.start}&end=${dateRange.end}`
      );
      const data = await res.json();
      const dateList: string[] = (Array.isArray(data) ? data : [])
        .map((item: { date?: unknown }) => normalizeDateString(item.date))
        .filter(Boolean)
        .sort((a: string, b: string) => b.localeCompare(a));
      setDates(dateList);
    } catch (error) {
      console.error('컨테이너 배차일보 날짜 목록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [open, dateRange.start, dateRange.end]);

  fetchDatesRef.current = fetchDates;

  useEffect(() => {
    const handler = (e: Event) => {
      const date = normalizeDateString((e as CustomEvent<{ date: string }>).detail?.date);
      if (!date) return;
      const range = dateRangeRef.current;
      const needsRangeUpdate = date < range.start || date > range.end;
      if (needsRangeUpdate) {
        setDateRange({
          start: date < range.start ? date : range.start,
          end: date > range.end ? date : range.end,
        });
      } else {
        void fetchDatesRef.current();
      }
    };
    window.addEventListener(CONTAINER_DISPATCH_DATE_SAVED_EVENT, handler);
    return () => window.removeEventListener(CONTAINER_DISPATCH_DATE_SAVED_EVENT, handler);
  }, []);

  useEffect(() => {
    void fetchDates();
  }, [fetchDates]);

  const fetchExportedDates = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;
    try {
      const res = await fetch(
        `/api/container-dispatch/exported-dates?start=${dateRange.start}&end=${dateRange.end}`
      );
      if (!res.ok) return;
      const list = await res.json();
      setExportedDates(Array.isArray(list) ? list : []);
    } catch {
      setExportedDates([]);
    }
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    if (!open) return;
    fetchExportedDates();
  }, [open, collapsed, fetchExportedDates]);

  useEffect(() => {
    if (!open) return;
    const handler = (data: { source?: string; operationType?: string }) => {
      if (data?.source === 'settlement' && data?.operationType === 'row_deleted') {
        void fetchExportedDates();
      }
    };
    socket.on('report-updated', handler);
    return () => {
      socket.off('report-updated', handler);
    };
  }, [open, fetchExportedDates]);

  useEffect(() => {
    setExportMessage(null);
    if (!open || !selectedDate) return;
    const checkDate = async () => {
      try {
        const res = await fetch(
          `/api/container-dispatch/exported-dates?start=${selectedDate}&end=${selectedDate}`
        );
        if (!res.ok) return;
        const list = await res.json();
        const exists = Array.isArray(list) && list.includes(selectedDate);
        setExportedDates(prev => {
          if (exists && !prev.includes(selectedDate)) return [...prev, selectedDate];
          if (!exists && prev.includes(selectedDate)) return prev.filter(d => d !== selectedDate);
          return prev;
        });
      } catch {
        /* ignore */
      }
    };
    checkDate();
  }, [selectedDate, open]);

  const handleDeleteDate = async (date: string) => {
    try {
      const res = await fetch(`/api/container-dispatch/${date}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: auth.currentUser?.uid,
          userEmail: auth.currentUser?.email ?? undefined,
        }),
      });
      if (!res.ok) throw new Error('삭제 실패');
      setDeleteTarget(null);
      setDeleteStep(null);
      const res2 = await fetch(
        `/api/container-dispatch/dates?start=${dateRange.start}&end=${dateRange.end}`
      );
      const data = await res2.json();
      const dateList: string[] = (Array.isArray(data) ? data : [])
        .map((item: { date?: unknown }) => normalizeDateString(item.date))
        .filter(Boolean)
        .sort((a: string, b: string) => b.localeCompare(a));
      setDates(dateList);
      if (date === selectedDate) {
        onSelectDate(undefined);
      }
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
      setDeleteTarget(null);
      setDeleteStep(null);
    }
  };

  const handleExportToSettlement = async () => {
    if (!selectedDate) {
      setExportMessage({ type: 'error', text: '보낼 컨테이너 배차일보 날짜를 먼저 선택하세요.' });
      return;
    }
    if (!window.confirm('지금 컨테이너 배차일보 데이터를 정산일보로 보내시겠습니까?')) return;

    setExportMessage(null);
    setExportLoading(true);
    try {
      const res = await fetch('/api/container-dispatch/export-to-settlement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setExportMessage({ type: 'error', text: data?.error || data?.message || '보내기 실패' });
        return;
      }
      const count = data?.exported ?? 0;
      setExportCountByDate(prev => ({ ...prev, [selectedDate]: count }));
      setExportedDates(prev => (prev.includes(selectedDate) ? prev : [...prev, selectedDate]));
      setExportMessage(null);
    } catch {
      setExportMessage({ type: 'error', text: '요청 중 오류가 발생했습니다.' });
    } finally {
      setExportLoading(false);
    }
  };

  const todayYmdSeoul = getTodayYmdSeoul();
  const showNewReportPanel = !loading && dates.length === 0;

  const handleStartNewReport = () => {
    const date = newReportDate.trim();
    if (!date) {
      alert('일보 날짜를 선택하세요.');
      return;
    }
    setDateRange(prev => ({
      start: date < prev.start ? date : prev.start,
      end: date > prev.end ? date : prev.end,
    }));
    onSelectDate(date);
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: collapsed ? 60 : 0,
      right: 0,
      width: collapsed ? 50 : 230,
      height: collapsed ? 'calc(100vh - 60px)' : '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e3e8ef 100%)',
      boxShadow: '-4px 0 16px rgba(0,0,0,0.10)',
      zIndex: 1000,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      borderTopLeftRadius: collapsed ? 8 : 16,
      borderBottomLeftRadius: 16,
      overflow: 'hidden',
      transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out, top 0.3s ease-in-out, border-top-left-radius 0.3s ease-in-out',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: collapsed ? '24px 8px 12px 8px' : '24px 18px 12px 18px',
        borderBottom: '1px solid #e0e6ed',
        background: 'rgba(255,255,255,0.95)',
      }}>
        {!collapsed && <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#222' }}>컨테이너 배차일보</h3>}
        <div style={{ display: 'flex', gap: 4 }}>
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 14,
                color: '#666',
                cursor: 'pointer',
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s, color 0.2s',
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#f0f0f0'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
              title={collapsed ? '펼치기' : '접기'}
            >
              {collapsed ? '◀' : '▶'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 15,
              color: '#888',
              cursor: 'pointer',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s, color 0.2s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#f0f0f0'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
            title="닫기"
          >
            ×
          </button>
        </div>
      </div>
      <div style={{
        padding: collapsed ? '16px 8px 0 8px' : '16px 14px 0 14px',
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        opacity: collapsed ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out',
      }}>
        {collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 4px' }}>
            {showNewReportPanel && (
              <>
                <input
                  type="date"
                  value={newReportDate}
                  onChange={e => setNewReportDate(e.target.value)}
                  style={{
                    padding: '4px 6px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 11,
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                  title="새 일보 날짜"
                />
                <button
                  type="button"
                  onClick={handleStartNewReport}
                  style={{
                    padding: '6px 4px',
                    border: 'none',
                    borderRadius: 4,
                    background: '#1976d2',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  title="새 일보"
                >
                  + 새 일보
                </button>
              </>
            )}
            <select
              value={selectedDate || ''}
              onChange={e => onSelectDate(e.target.value || undefined)}
              style={{
                padding: '4px 6px',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 12,
                background: '#fff',
                cursor: 'pointer',
              }}
              title="날짜 선택"
            >
              <option value="">날짜 선택</option>
              {dates.map(date => (
                <option key={date} value={date}>
                  {date} ({getKoreanDayShort(date)})
                </option>
              ))}
            </select>
          </div>
        )}
        {!collapsed && (
          <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              marginBottom: 18,
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              padding: '10px 8px',
            }}>
              <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 2, fontSize: 14 }}>기간 선택</div>
              <input
                type="date"
                value={dateRange.start}
                onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
                style={{
                  padding: '4px 6px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 13,
                  marginBottom: 4,
                }}
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
                style={{
                  padding: '4px 6px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 13,
                }}
              />
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={handleExportToSettlement}
                  disabled={exportLoading || !selectedDate || (selectedDate != null && exportedDates.includes(selectedDate))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    cursor: exportLoading || !selectedDate || (selectedDate != null && exportedDates.includes(selectedDate)) ? 'not-allowed' : 'pointer',
                    opacity: selectedDate != null && exportedDates.includes(selectedDate) ? 0.8 : 1,
                  }}
                >
                  {exportLoading
                    ? '보내는 중…'
                    : selectedDate != null && exportedDates.includes(selectedDate)
                      ? '보내기 완료 (해당 날짜)'
                      : '데이터보내기 (정산용)'}
                </button>
                {(() => {
                  const showSuccessForDate = selectedDate && exportedDates.includes(selectedDate) && exportCountByDate[selectedDate] != null;
                  const showError = exportMessage?.type === 'error';
                  if (!showSuccessForDate && !showError) return null;
                  return (
                    <p style={{
                      marginTop: 6,
                      marginBottom: 0,
                      fontSize: 12,
                      color: showError ? '#c62828' : '#2e7d32',
                    }}
                    >
                      {showError ? exportMessage!.text : `보내기 완료 (${exportCountByDate[selectedDate!]}건)`}
                    </p>
                  );
                })()}
              </div>
            </div>
            {showNewReportPanel && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                marginBottom: 12,
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                padding: '10px 8px',
              }}>
                <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 2, fontSize: 14 }}>새 일보</div>
                <input
                  type="date"
                  value={newReportDate}
                  onChange={e => setNewReportDate(e.target.value)}
                  style={{
                    padding: '4px 6px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                />
                <button
                  type="button"
                  onClick={handleStartNewReport}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: 4,
                    background: '#1976d2',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  + 새 일보
                </button>
                <p style={{ margin: 0, fontSize: 11, color: '#78909c', lineHeight: 1.4 }}>
                  날짜를 선택한 뒤 데이터를 입력하면 저장 시 목록에 추가됩니다.
                </p>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingLeft: 4, paddingRight: 2, boxSizing: 'border-box' }}>
              {loading ? (
                <div style={{ color: '#888', textAlign: 'center', marginTop: 40 }}>로딩 중...</div>
              ) : showNewReportPanel ? (
                <div style={{ color: '#888', textAlign: 'center', marginTop: 24, fontSize: 13, lineHeight: 1.5, padding: '0 8px' }}>
                  저장된 일보가 없습니다.<br />위에서 새 일보를 만드세요.
                </div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {dates.map(date => {
                    const isToday = date === todayYmdSeoul;
                    const isSelected = date === selectedDate;
                    const dateButton = (
                      <button
                        type="button"
                        onClick={() => onSelectDate(date)}
                        style={{
                          width: '100%',
                          padding: '10px 0',
                          border: isSelected ? '2px solid #0d47a1' : 'none',
                          borderRadius: 8,
                          background: isSelected ? '#bbdefb' : '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 600,
                          fontSize: 15,
                          cursor: 'pointer',
                          boxShadow: '0 1px 4px rgba(30,60,90,0.04)',
                          transition: 'background 0.2s, color 0.2s',
                          textAlign: 'center',
                          boxSizing: 'border-box',
                        }}
                        onMouseOver={e => {
                          if (isSelected) return;
                          e.currentTarget.style.background = '#1976d2';
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = isSelected ? '#bbdefb' : '#e3f2fd';
                          e.currentTarget.style.color = '#1976d2';
                        }}
                      >
                        {(() => {
                          const dayKor = getKoreanDayShort(date);
                          const dayColorMap: Record<string, string> = {
                            월: '#ff9800',
                            화: '#388e3c',
                            수: '#00bcd4',
                            목: '#7b1fa2',
                            금: '#d32f2f',
                            토: '#795548',
                            일: '#f44336',
                          };
                          return (
                            <>
                              {date} (
                              <span style={{ color: dayColorMap[dayKor] || '#1976d2', fontWeight: 700 }}>{dayKor}</span>)
                            </>
                          );
                        })()}
                      </button>
                    );
                    return (
                      <li key={date} style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {isToday ? (
                          <div
                            title="오늘 날짜"
                            style={{
                              width: 'calc(100% - 28px)',
                              padding: 3,
                              borderRadius: 11,
                              background: '#0d47a1',
                              boxSizing: 'border-box',
                              flexShrink: 0,
                            }}
                          >
                            {dateButton}
                          </div>
                        ) : (
                          <div style={{ width: 'calc(100% - 28px)', flexShrink: 0 }}>{dateButton}</div>
                        )}
                        <button
                          type="button"
                          onClick={() => { setDeleteTarget(date); setDeleteStep(1); }}
                          style={{
                            marginLeft: 4,
                            background: 'none',
                            border: 'none',
                            color: '#d32f2f',
                            fontSize: 13,
                            cursor: 'pointer',
                            width: 13,
                            height: 13,
                            minWidth: 13,
                            minHeight: 13,
                            padding: 0,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s',
                          }}
                          title="컨테이너 배차일보 삭제"
                        >
                          ❌
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
      {deleteTarget && deleteStep === 1 && (
        <div style={{
          position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 20000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 320, boxShadow: '0 2px 16px rgba(0,0,0,0.15)' }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 18, color: '#d32f2f' }}>
              컨테이너 배차일보를 삭제하면 해당 날짜의 모든 내용이 모두 삭제됩니다.<br />계속 진행하시겠습니까?
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setDeleteStep(null); setDeleteTarget(null); }} style={{ background: '#eee', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 500, cursor: 'pointer' }}>아니오</button>
              <button type="button" onClick={() => setDeleteStep(2)} style={{ background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>예</button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && deleteStep === 2 && (
        <div style={{
          position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 20000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 320, boxShadow: '0 2px 16px rgba(0,0,0,0.15)' }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 18, color: '#d32f2f' }}>
              정말로 삭제하시겠습니까?<br />선택한 날짜의 모든 컨테이너 배차내역이 삭제되며 복구가 불가능합니다.
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setDeleteStep(null); setDeleteTarget(null); }} style={{ background: '#eee', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 500, cursor: 'pointer' }}>아니오</button>
              <button type="button" onClick={() => handleDeleteDate(deleteTarget)} style={{ background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>예</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContainerDispatchSidebar;
